import { colors } from "../../../../../styles/design-system";

export type DimensionTone = "success" | "brand" | "warning" | "error";
export type DimensionStatus = "Strong" | "Good" | "Needs work" | "Critical";

export interface EvaluatorIssueWithEntity {
  id: string;
  description: string;
  entityId: string | null;
  entityType: string | null;
  title: string;
}

export interface GapAnalysisDimension {
  id: string;
  label: string;
  score: number;
  calculatedScore?: number;
  maxScore?: number;
  status: DimensionStatus;
  issues: number;
  tone: DimensionTone;
  tooltip: string;
  informational?: boolean;
  issuesList: string[];
  issuesWithEntity?: EvaluatorIssueWithEntity[];
  summary?: string | null;
  detailedFindings?: string | null;
  evaluatedEntities?: number;
  failedEntities?: number;
  goodEntities?: number;
}

export interface RubricScorecardCategory {
  label: string;
  earned: number;
  max: number;
  tone: "brand" | "success";
}

export const GAP_DIMENSIONS: GapAnalysisDimension[] = [
  {
    id: "clo-coverage",
    label: "CLO coverage",
    score: 100,
    status: "Strong",
    issues: 1,
    tone: "success",
    tooltip:
      "Checks whether each course-level learning outcome is referenced by at least one module's content. Outcomes that no module mentions are flagged as uncovered.",
    issuesList: ["The Course Overview does not include measurable learning objectives that students can reference."],
  },
  {
    id: "rubric-quality",
    label: "Rubric Alignment",
    score: 74,
    status: "Needs work",
    issues: 5,
    tone: "warning",
    tooltip: "Measures alignment with the active rubric. Surfaces categories scoring below the 80% threshold.",
    issuesList: [
      "Several rubric criteria need clearer performance levels and measurable expectations before students can act on them.",
      "Rubric language is inconsistent across categories.",
      "Some rubric points are not tied to observable evidence.",
      "Assessment weighting is unclear for a subset of graded work.",
      "Feedback criteria should distinguish acceptable and exemplary submissions.",
    ],
  },
  {
    id: "content-freshness",
    label: "Content freshness",
    score: 60,
    status: "Needs work",
    issues: 3,
    tone: "warning",
    tooltip:
      "Tracks how recently activities have been reviewed or updated. Modules whose activities haven't been refreshed in this review surface as stale.",
    issuesList: [
      "Foundations: How to Look at Art hasn't been touched in this review yet - its activities haven't been refreshed.",
      "Renaissance & Baroque hasn't been touched in this review yet - its activities haven't been refreshed.",
      "The Module 3 reading is drawn from a 2018 textbook chapter and predates significant reattribution scholarship around women Impressionists.",
    ],
  },
  {
    id: "concept-freshness",
    label: "Concept freshness",
    score: 60,
    status: "Needs work",
    issues: 1,
    tone: "warning",
    tooltip:
      "Flags the gap between the latest research or scholarship in this field and what the course currently teaches. Separate from Content freshness, which tracks broken links and stale source material.",
    issuesList: [
      'The Module 4 reading list cites only the mid-century MoMA-canon framing (Greenberg "Avant-Garde and Kitsch," Rosenberg "American Action Painters," early Krauss). The field has moved substantially in the last decade - Bishop\'s "Artificial Hells" (2012), Demos\'s "Beyond the World\'s End" (2020), Mercer\'s "Travel & See" (2016), and the Tate Modern\'s "Modernism in the Plural" curatorial reframe (2023) are now standard at peer institutions for any module pretending to cover modernism honestly.',
    ],
  },
  {
    id: "industry-alignment",
    label: "Industry alignment",
    score: 68,
    status: "Needs work",
    issues: 2,
    tone: "warning",
    tooltip: "Maps course activities to current industry standards and skills, informed by O*NET occupational data.",
    informational: true,
    issuesList: [
      "No industry-standard framework is mapped to this course. Faculty input or an institutional library is needed for real alignment scoring.",
      "Course Overview introduces concepts that align well with industry expectations but isn't tagged - adding tags would improve alignment reporting.",
    ],
  },
  {
    id: "accessibility",
    label: "Accessibility",
    score: 50,
    status: "Critical",
    issues: 3,
    tone: "error",
    tooltip:
      "Checks activity titles and descriptions for HTML accessibility issues. Multimedia (images, videos, slides, PDFs) and external content require manual verification.",
    issuesList: [
      "The Renaissance Slide Deck is missing alt text on 14 of 40 slides containing artwork images. Screen reader users cannot access the visual content the rest of the module depends on.",
      "The Baroque lecture handout is a scanned PDF with no OCR layer. Screen readers cannot parse any of the text or image captions, making the entire document inaccessible.",
      "The 22-minute Romanticism documentary on YouTube has no captions and no transcript. Deaf and hard-of-hearing students cannot access any of the spoken content.",
    ],
  },
  {
    id: "ai-vulnerability",
    label: "AI vulnerability",
    score: 15,
    status: "Critical",
    issues: 5,
    tone: "error",
    tooltip:
      "Identifies graded assessments that students could complete with generative AI without demonstrating learning. Flags untimed multiple-choice, take-home write-ups, and open-text submissions without process artifacts.",
    issuesList: [
      "Curatorial Project: A Virtual Modernist Gallery (200 pts) - final deliverable accepts text without process artifacts.",
      "Final Essay: Choose a Contemporary Artist (150 pts) - take-home write-up with no in-class anchor or oral defense.",
      "Formal Analysis Paper: A Renaissance Painting of Your Choice (100 pts) - take-home write-up with no in-class anchor or oral defense.",
      "Pair the Curatorial Project (200 pts) with a brief oral defense - a 5-minute recorded walkthrough where students narrate their curatorial logic, defend two label choices, and respond to one instructor follow-up question. Closes a major AI-vulnerability gap on the heaviest assessment in the course.",
      "Restructure the Final Essay so students submit a graded first draft two weeks before the final, then complete a peer review of two classmates' drafts using a guided rubric. The final submission must include a short reflection on which peer feedback was incorporated. The drafted-and-annotated process makes AI completion measurably harder.",
    ],
  },
  {
    id: "cognitive-load",
    label: "Cognitive Load & Credit Hours",
    score: 82,
    status: "Good",
    issues: 2,
    tone: "brand",
    tooltip:
      "Grounded in Cognitive Load Theory (Sweller). Checks that the course manages intrinsic load and minimizes extraneous load — weekly credit-hour distribution, per-module activity density, pacing of concept introduction, and deadline spread (deadlines avoid weekends and holidays). Also flags walls of text, overly dense modules, content that needs chunking, excessive activities per unit, and missing breaks or buffer weeks.",
    issuesList: [
      "Estimated workload (~52 hrs) is below the 3-credit expectation (~135 hrs). Intrinsic load may be under-served - students aren't getting enough sustained practice to consolidate the material.",
      "Renaissance & Baroque carries 5 activities - students may not have time to engage deeply with each in a single instructional week.",
    ],
  },
  {
    id: "institutional-guidelines",
    label: "Institutional Guidelines",
    score: 71,
    status: "Needs work",
    issues: 1,
    tone: "warning",
    tooltip:
      "Evaluates course content against your institution’s uploaded policy documents (e.g., faith integration, inclusive language). Surfaces activities that conflict with or omit guidance from those policies.",
    issuesList: [
      "The Introductions discussion prompt lacks the community-norms preamble required by Inclusive Language Standards. Students aren’t told to use person-first, culturally-sensitive language when sharing background, identity, or beliefs — exactly the moments the policy is meant to cover.",
    ],
  },
];

export const TONE_COLORS: Record<DimensionTone, string> = {
  success: colors.success[500],
  brand: colors.brand[500],
  warning: colors.warning[500],
  error: colors.error[500],
};

export function getDimensionDotColor(dimension: GapAnalysisDimension): string {
  if (dimension.status === "Critical") {
    return colors.error[500];
  }

  if (dimension.status === "Needs work") {
    return colors.warning[500];
  }

  return colors.success[500];
}

export const RUBRIC_SCORECARD_CATEGORIES: RubricScorecardCategory[] = [
  { label: "Course Design", earned: 9, max: 13, tone: "brand" },
  { label: "Assessments", earned: 8, max: 13, tone: "brand" },
  { label: "Engagement", earned: 11, max: 12, tone: "success" },
];

export const RUBRIC_SCORECARD_ISSUES = [
  "Course Design scored 9 / 13 - below the 80% rubric threshold.",
  "Assessments scored 8 / 13 - below the 80% rubric threshold.",
  "The Course Overview does not list required materials, textbooks, or museum-partner expectations.",
  "The Course Overview does not include instructor office hours or preferred contact methods.",
  "The Curatorial Project is the heaviest assessment in the course (200 points) but lacks a rubric, a deliverable spec, and any guidance on how to write a curatorial statement or wall label.",
];

export const DIMENSION_ID_ALIASES: Record<string, string> = {
  "content-objective-mapping": "clo-coverage",
  "rubric-alignment": "rubric-quality",
  "stale-content": "content-freshness",
  "stale-concepts": "concept-freshness",
  "industry-alignment": "industry-alignment",
  accessibility: "accessibility",
  "ai-vulnerability": "ai-vulnerability",
  "institutional-guidelines": "institutional-guidelines",
  "cognitive-load": "cognitive-load",
  "cognitive-load-credit-hours": "cognitive-load",
  "cognitive-load-credit-hours-v2": "cognitive-load",
};
